
import { useState } from 'react';

export default function CTA() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    quantity: '1',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d5ud8kb0o41p73073ecg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', quantity: '1', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <section id="cta" className="py-24 bg-gradient-to-b from-white to-amber-50/30">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <span className="text-amber-600 text-sm font-semibold tracking-widest uppercase mb-4 block" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              Experience the Difference
            </span>
            <h3 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Bring Carthaginian Treasure to Your Table
            </h3>
            <p className="text-lg text-gray-700 leading-relaxed mb-8" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              Experience the authentic taste of <strong>Mediterranean tradition</strong>. Order your bottle of <strong>Carthaginian Treasure</strong> today and discover why our <strong>extra virgin olive oil</strong> is treasured by those who appreciate true quality.
            </p>
            
            <div className="space-y-4 mb-8">
              <div className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-amber-600 text-xl mt-1"></i>
                <p className="text-gray-700" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  <strong>100% Wild</strong> - From ancient wild olive trees
                </p>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-amber-600 text-xl mt-1"></i>
                <p className="text-gray-700" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  <strong>Single Origin</strong> - From our family farms in Tunisia
                </p>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-amber-600 text-xl mt-1"></i>
                <p className="text-gray-700" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  <strong>Cold-Pressed</strong> - Maximum nutrition and flavor
                </p>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-amber-600 text-xl mt-1"></i>
                <p className="text-gray-700" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  <strong>Early Harvest</strong> - Peak polyphenol content
                </p>
              </div>
            </div>

            <div className="relative h-64 rounded-xl overflow-hidden shadow-xl">
              <img 
                src="https://public.readdy.ai/ai/img_res/69e6e51bd609ac99276c688949ce4217.jpg" 
                alt="Carthaginian Treasure olive oil bottle"
                className="w-full h-full object-cover object-center"
              />
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-10">
            <h4 className="text-2xl font-bold text-gray-900 mb-6 text-center" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Order Your Bottle Today
            </h4>
            
            <form id="purchase-form" data-readdy-form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  Phone Number
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                />
              </div>

              <div>
                <label htmlFor="quantity" className="block text-sm font-medium text-gray-700 mb-2" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  Quantity *
                </label>
                <select
                  id="quantity"
                  name="quantity"
                  required
                  value={formData.quantity}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm cursor-pointer"
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                >
                  <option value="1">1 Bottle</option>
                  <option value="2">2 Bottles</option>
                  <option value="3">3 Bottles</option>
                  <option value="6">6 Bottles (Case)</option>
                  <option value="12">12 Bottles (2 Cases)</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  Additional Notes
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={4}
                  maxLength={500}
                  value={formData.message}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm resize-none"
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                  placeholder="Any special requests or questions?"
                />
                <p className="text-xs text-gray-500 mt-1" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  {formData.message.length}/500 characters
                </p>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-amber-600 text-white py-4 rounded-lg font-semibold hover:bg-amber-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Order Inquiry'}
              </button>

              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-sm" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  Thank you! We'll contact you shortly to complete your order.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  Something went wrong. Please try again or contact info@carthtreasure.com directly.
                </div>
              )}
            </form>

            <p className="text-xs text-gray-500 text-center mt-6" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              We'll contact you to finalize your order and arrange delivery
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
